<template>
  <div class="py-3">
    <div class="p-toolbar">
      <div class="p-toolbar-item p-search">
        <p-search-input
          v-model="search"
          :items="searchSuggestions"
        ></p-search-input>
      </div>
      <div v-if="hasAccess()" class="p-toolbar-item p-right">
        <v-btn
          class="p-btn-rounded mr-4"
          large
          rounded
          @click.stop="openEditDialog(null)"
        >
          {{ $t('eventCategory.createCategory') }}
        </v-btn>
      </div>
    </div>

    <v-row>
      <v-col>
        <v-data-table
          :headers="tableHeaders"
          :search="search"
          :items="eventCategoriesComputed"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          class="p-no-borders"
          hide-default-footer
          @page-count="pageCount = $event"
        >
          <template v-slot:[`item.changed`]="{ item }">
            <v-tooltip v-if="item.changed" bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on">
                  <span v-if="item.changed" class="item-changed"></span>
                </v-btn>
              </template>
              {{ $t('eventCategory.table.changedTooltip') }}
            </v-tooltip>
          </template>
          <template v-if="hasAccess()" v-slot:[`item.actions`]="{ item }">
            <div class="p-table-row-actions">
              <p-btn-icon type="edit" @click="openEditDialog(item)"></p-btn-icon>
              <p-btn-icon type="delete" @click="openDeleteDialog(item)"></p-btn-icon>
            </div>
          </template>
        </v-data-table>

        <v-pagination
          v-if="eventCategories.length > 0"
          v-model="page"
          :length="pageCount"
          :total-visible="15"
          class="mt-5"
        ></v-pagination>
      </v-col>
    </v-row>

    <event-category-edit-dialog
      :dialog.sync="editDialog"
      :eventCategory="editedEventCategory"
    ></event-category-edit-dialog>

    <p-delete-dialog
      :dialog.sync="deleteDialog"
      :item="deletedEventCategory"
      :item-data="deletedEventCategoryData"
      :load-relations="loadRelations"
      :delete-item="deleteEventCategory"
      @itemDeleted="onItemDeleted"
    ></p-delete-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import PSearchInput from '@/components/PSearchInput'
import PBtnIcon from '@/components/PBtnIcon'
import PDeleteDialog from '@/components/PDeleteDialog'

import EventCategoryEditDialog from './EventCategoryEditDialog'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import accessMixin from '@/mixins/access.mixin'

import eventBus from '@/eventBus'


export default {
  components: {
    PSearchInput,
    PBtnIcon,
    PDeleteDialog,
    EventCategoryEditDialog
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    accessMixin
  ],


  data () {
    return {
      search: '',
      editDialog: false,
      editedEventCategory: null,
      page: 1,
      pageCount: 0,
      itemsPerPage: 15,
      deleteDialog: false,
      deletedEventCategory: null,
      deletedEventCategoryData: null
    }
  },


  computed: {
    ...mapGetters('eventCategory', ['eventCategories']),
    ...mapGetters('changes', ['isItemChanged']),

    eventCategoriesComputed () {
      return this.eventCategories.map(eventCategory => {
        return {
          ...eventCategory,
          changed: this.isItemChanged(eventCategory.id)
        }
      })
    },

    tableHeaders () {
      return [
        { text: this.$t('eventCategory.table.name'), value: 'name' },
        { text: '', value: 'changed' },
        { text: '', value: 'actions', align: 'end', width: 75, sortable: false, filterable: false }
      ]
    },

    searchSuggestions () {
      const items = this.eventCategories.map(x => x.name)
      return items
    }
  },


  methods: {
    ...mapActions('eventCategory', ['loadEventCategories', 'loadRelations', 'deleteEventCategory']),


    async searchEventCategories (val) {
      this.search = val
    },

    initEventBus () {
      eventBus.$on('goToEntity', async (entityTitle) => {
        this.searchEventCategories(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        await this.loadEventCategories()
      })
    },

    openEditDialog (eventCategory) {
      this.editedEventCategory = eventCategory
      this.editDialog = true
    },

    openDeleteDialog (eventCategory) {
      try {
        if (eventCategory) {
          this.deletedEventCategory = eventCategory
          this.deletedEventCategoryData = {
            type: this.$t('entity.eventCategory'),
            title: eventCategory.name
          }
        }
        this.deleteDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    onItemDeleted () {
      this.showSnackbarSuccess(this.$t('eventCategory.categoryDeleted'))
    },

    async deleteCategory (eventCategory) {
      try {
        const confirm = await this.$confirm(this.$t('eventCategory.deleteCategoryConfirm', eventCategory), { trueText: this.$t('delete') })
        if (confirm === true) {
          this.showSnackbarProcessing()
          await this.deleteEventCategory(eventCategory.id)
          this.showSnackbarSuccess(this.$t('eventCategory.categoryDeleted'))
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  },

  async created () {
    try {
      this.initEventBus()
      this.startPageLoading()
      await this.loadEventCategories()
      if (this.$router.currentRoute.params?.goto) {
        await this.searchEventCategories(this.$router.currentRoute.params?.goto)
      }
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
